import React, { useMemo } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { formatNull, formatDate, getNameForImportantEventsType } from '../../Utils/GlobalUtils';

import WarningPanel from '../WarningPanel';
import Loading from '../Loading';

import styles from '../../styles/DynamicModal.module.css';
import miniTable from '../../styles/MiniTables.module.css';

import IconContainer from '../../Components/IconContainer';
import { ReactComponent as XIcon } from '../../icons/x-close.svg';
import { ReactComponent as DotIcon } from '../../icons/dot.svg';
import { ReactComponent as CaretDownIcon } from '../../icons/caret-down.svg';
import { ReactComponent as CaretUpIcon } from '../../icons/caret-up.svg';

export default function FilterImportantEvents({ field=null, filters, setFilters, DEFAULT_FILTERS, closeModal }) {
    const [entries, setEntries] = React.useState([]);
    const [reload, setReload] = React.useState(true);
    const [warning, setWarning] = React.useState('');
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const token = localStorage.getItem('token');

        axios.get(`/api/get-one-field-entries-in-all-cases?field=${field}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        })
        .then(response => {
            setEntries(response.data);
        })
        .catch(error => {
            console.error('Ошибка при получении данных:', error);
            setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
        }).finally(() => {
            setLoading(false);
            setReload(false);
        })
    }, [reload]);

    const [searchNumber, setSearchNumber] = React.useState('');

    const filteredNumbers = useMemo(() => {
        const query = searchNumber.trim().toLowerCase();
        if (!query) return entries;

        return entries.filter(entry => {
            const fullEntry = `${entry.number}`.toLowerCase();
            return fullEntry.includes(query) || entry.number.toLowerCase().includes(query);
        });
    }, [entries, searchNumber]);

    const [searchDebtor, setSearchDebtor] = React.useState('');

    const filteredDebtors = useMemo(() => {
        const query = searchDebtor.trim().toLowerCase();
        if (!query) return entries;

        return entries.filter(entry => {
            const fullEntry = `${entry.name}`.toLowerCase();
            return fullEntry.includes(query) || entry.name.toLowerCase().includes(query);
        });
    }, [entries, searchDebtor]);

    const [searchCountPretenders, setSearchCountPretenders] = React.useState('');

    const filteredCountPretenders = useMemo(() => {
        const query = searchCountPretenders.trim();
        if (!query) return entries;

        return entries.filter(entry => {
            const fullEntry = `${entry.applications_count}`;
            return fullEntry.includes(query);
        });
    }, [entries, searchCountPretenders]);



    const allSelectedNumbers =
        filteredNumbers.length > 0 &&
        filteredNumbers.every(entry => filters.numbers.includes(entry.number));

    const handleSelectAllNumbers = () => {
        setFilters(prev => ({
            ...prev,
            numbers: allSelectedNumbers
                ? prev.numbers.filter(
                    number => !filteredNumbers.some(entry => entry.number === number)
                )
                : [
                    ...new Set([
                        ...prev.numbers,
                        ...filteredNumbers.map(entry => entry.number)
                    ])
                ]
        }));
    };

    const allSelectedDebtors =
        filteredDebtors.length > 0 &&
        filteredDebtors.every(entry => filters.debtors.includes(entry.id));

    const handleSelectAllDebtors = () => {
        setFilters(prev => ({
            ...prev,
            debtors: allSelectedDebtors
                ? prev.debtors.filter(
                    id => !filteredDebtors.some(entry => entry.id === id)
                )
                : [
                    ...new Set([
                        ...prev.debtors,
                        ...filteredDebtors.map(entry => entry.id)
                    ])
                ]
        }));
    };

    React.useEffect(() => {
        if (filters.mailing_status=='Письма не отправлены') {
            setFilters((prev) => ({
                ...prev,
                date_mailing_from: '',
                date_mailing_to: '',
            }));
        }
    }, [filters.mailing_status])

    const [dateMailing, setDateMailing] = React.useState([]);

    React.useEffect(() => {
        if (dateMailing.length > 0) {
            const sorted = [...dateMailing].sort((a, b) => new Date(a) - new Date(b));
            if (field=='date_mailing') {
                setFilters(f => ({
                    ...f,
                    date_mailing_from: sorted[0],
                    date_mailing_to: sorted[sorted.length - 1],
                }));
            } else if (field=='date_consent_deadline') {
                setFilters(f => ({
                    ...f,
                    date_court_from: sorted[0],
                    date_court_to: sorted[sorted.length - 1],
                }));
            } else if (field=='date_court') {
                setFilters(f => ({
                    ...f,
                    date_court_from: sorted[0],
                    date_court_to: sorted[sorted.length - 1],
                }));
            }
        } 
    }, [dateMailing]);

    const [selectedMonths, setSelectedMonths] = React.useState([]);

    const monthNames = [
        'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];

    const formatLocal = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const now = new Date();

    const months = Array.from({ length: 12 }, (_, i) => {
        // i=0 - текущий месяц, дальше в прошлое
        const targetDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const year = targetDate.getFullYear();
        const monthIndex = targetDate.getMonth();

        const from = new Date(year, monthIndex, 1);
        const to = new Date(year, monthIndex + 1, 0);

        return {
            key: `${year}-${String(monthIndex + 1).padStart(2, '0')}`, // "2026-07"
            label: `${monthNames[monthIndex]} ${year}`,
            from: formatLocal(from),
            to: formatLocal(to),
        };
    });

    React.useEffect(() => {
        if (filters.date_court_to < filters.date_court_from) {
            setFilters((prev) => ({
                ...prev,
                date_court_to: filters.date_court_from,
            }));
        }
    }, [filters.date_court_to]);
    React.useEffect(() => {
        if (filters.date_am_appointment_r_to < filters.date_am_appointment_r_from) {
            setFilters((prev) => ({
                ...prev,
                date_am_appointment_r_to: filters.date_am_appointment_r_from,
            }));
        }
    }, [filters.date_am_appointment_r_to]);


    const handleChange = (field) => (e) => {
        const value = e.target.value;

        setFilters((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const addFilter = () => {
        closeModal();
    };

    const resetFilters = () => {
        setFilters(DEFAULT_FILTERS);
        closeModal();
    };

    const [isDateMailingOpen, setIsDateMailingOpen] = React.useState(false);


    return (
        <div className={styles.liningOver}>
            <motion.div
                className={styles.lining} 
                onClick={(e) => e.stopPropagation()}
                initial={{ scale: '97%', opacity: 0 }}
                animate={{ scale: '100%', opacity: 1 }}
                exit={{ scale: '97%', opacity: 0 }}
                transition={{ duration: 0.3 }}
            >
                <div className={styles.header}>
                    <h1>Фильтр по журналу событий</h1>
                    <IconContainer Icon={XIcon} onClick={closeModal} size = '20' color = 'black' className = '' />
                </div>
                <div className={styles.main} style={{height: '70vh'}}>
                    {field=='type' &&
                    <>
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Тип события</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${!filters.type && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        type: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='labelInputRight'>
                            <div className='radioSwitchWrapper verticalRSWrapper'>
                                <label className={`radioSwitchLabel ${filters.type === 'add_verification_code' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='add_verification_code'
                                        checked={filters.type === 'add_verification_code'}
                                        onChange={handleChange('type')}
                                    />
                                    <span>{getNameForImportantEventsType('add_verification_code')}</span>
                                </label>
                                <label className={`radioSwitchLabel ${filters.type === 'file_deleted' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='file_deleted'
                                        checked={filters.type === 'file_deleted'}
                                        onChange={handleChange('type')}
                                    />
                                    <span>{getNameForImportantEventsType('file_deleted')}</span>
                                </label>
                                <label className={`radioSwitchLabel ${filters.type === 'change_system_settings' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='change_system_settings'
                                        checked={filters.type === 'change_system_settings'}
                                        onChange={handleChange('type')}
                                    />
                                    <span>{getNameForImportantEventsType('change_system_settings')}</span>
                                </label>
                                <label className={`radioSwitchLabel ${filters.type === 'am_has_been_selected' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='am_has_been_selected'
                                        checked={filters.type === 'am_has_been_selected'}
                                        onChange={handleChange('type')}
                                    />
                                    <span>{getNameForImportantEventsType('am_has_been_selected')}</span>
                                </label>
                                <label className={`radioSwitchLabel ${filters.type === 'user_role_changed' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='user_role_changed'
                                        checked={filters.type === 'user_role_changed'}
                                        onChange={handleChange('type')}
                                    />
                                    <span>{getNameForImportantEventsType('user_role_changed')}</span>
                                </label>
                                <label className={`radioSwitchLabel ${filters.type === 'add_appointment_agreement' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='add_appointment_agreement'
                                        checked={filters.type === 'add_appointment_agreement'}
                                        onChange={handleChange('type')}
                                    />
                                    <span>{getNameForImportantEventsType('add_appointment_agreement')}</span>
                                </label>
                            </div>
                        </div>
                        </div>    
                    </div>
                    </>}

                    {field=='debtors' &&
                    <>
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Тип карточки</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${!filters.category && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        category: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='radioSwitchWrapper'>
                                <label className={`radioSwitchLabel ${filters.category === 'Назначения' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='Назначения'
                                        checked={filters.category === 'Назначения'}
                                        onChange={handleChange('category')}
                                    />
                                    <span>Назначения</span>
                                </label>
                                <label className={`radioSwitchLabel ${filters.category === 'Жалобы' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='Жалобы'
                                        checked={filters.category === 'Жалобы'}
                                        onChange={handleChange('category')}
                                    />
                                    <span>Жалобы</span>
                                </label>
                                <label className={`radioSwitchLabel ${filters.category === 'Общие' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='Общие'
                                        checked={filters.category === 'Общие'}
                                        onChange={handleChange('category')}
                                    />
                                    <span>Общие</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <span className='horizontalLine'></span>
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Должник</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${filters.debtors.length===0 && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        debtors: [],
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='inputWrapper'>
                                <input 
                                    type='text' 
                                    value={searchDebtor}
                                    onChange={(e) => setSearchDebtor(e.target.value)}
                                    placeholder='ФИО или наименование' 
                                    minLength='' 
                                    maxLength='150' 
                                    required 
                                />
                            </div>

                            {loading ? <Loading />
                            : warning ? <WarningPanel text={warning} />
                            :
                            <>
                            {!searchDebtor && <p className='description'>Первые 400 записей</p>}
                            {filteredDebtors.length!==0 &&
                            <div className={miniTable.table}>
                                <div
                                    className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                    style={{ gridTemplateColumns: '99fr 1fr' }}
                                    onClick={handleSelectAllDebtors}
                                >
                                    <div>
                                        Выбрать все {searchDebtor && 'из показанных'}
                                    </div>
                                    <div className={miniTable.actions}>
                                        <input 
                                            type='checkbox'
                                            checked={allSelectedDebtors}
                                            readOnly={true}
                                        />
                                    </div>
                                </div>
                            </div>}
                            <div className={miniTable.table}>
                                {filteredDebtors.length===0 ? <WarningPanel text='Ничего не найдено' inTable={true} />
                                : filteredDebtors.map((entry, index) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={index}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setFilters(prev => ({
                                                ...prev,
                                                debtors: prev.debtors.includes(entry.id)
                                                    ? prev.debtors.filter(n => n !== entry.id)
                                                    : [...prev.debtors, entry.id]
                                            }));
                                        }}
                                    >
                                        <div>
                                            {entry.name} ({entry.occurrences})
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={filters.debtors.includes(entry.id)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            </>}
                        </div>
                    </div>
                    </>}

                    {field=='ams' &&
                    <>
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Статус управляющего</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${(!filters.status_am) && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        status_am: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='radioSwitchWrapper'>
                                <label className={`radioSwitchLabel ${filters.status_am === 'Управляющий не выбран' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='Управляющий не выбран'
                                        checked={filters.status_am === 'Управляющий не выбран'}
                                        onChange={handleChange('status_am')}
                                    />
                                    <span>Управляющий не выбран</span>
                                </label>
                                <label className={`radioSwitchLabel ${filters.status_am === 'Управляющий выбран' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='Управляющий выбран'
                                        checked={filters.status_am === 'Управляющий выбран'}
                                        onChange={handleChange('status_am')}
                                    />
                                    <span>Управляющий выбран</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    {filters.status_am=='Управляющий выбран' &&
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Арбитражные управляющие</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${(filters.ams.length===0) && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        ams: [],
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <a className='description'>Временно недоступно</a>
                        </div>
                    </div>}
                    </>}

                    {field=='date_court' &&
                    <>
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Дата судебного заседания об утверждении АУ</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${(!filters.date_court_from && !filters.date_court_to) && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        date_court_from: '',
                                        date_court_to: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='inputTwoDateWrapper'>
                                <input type='date' 
                                    value={filters.date_court_from?.split('T')[0]}
                                    onChange={handleChange('date_court_from')} 
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required 
                                />
                                <p>—</p>
                                <input type='date' 
                                    value={filters.date_court_to?.split('T')[0]}
                                    onChange={handleChange('date_court_to')} 
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required 
                                />
                            </div>
                            <p className='description'>Быстрый выбор интервала по крайним 12 месяцам</p>
                            <div className={miniTable.table}>
                                {months.map((month) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={month.key}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setSelectedMonths(prev => {
                                                const newSelected = prev.includes(month.key)
                                                    ? prev.filter(k => k !== month.key)
                                                    : [...prev, month.key];

                                                if (newSelected.length === 0) {
                                                    setFilters(f => ({ ...f, date_court_from: '', date_court_to: '' }));
                                                } else {
                                                    const selectedRanges = months.filter(m => newSelected.includes(m.key));
                                                    const from = selectedRanges.reduce((min, m) => m.from < min ? m.from : min, selectedRanges[0].from);
                                                    const to = selectedRanges.reduce((max, m) => m.to > max ? m.to : max, selectedRanges[0].to);
                                                    setFilters(f => ({ ...f, date_court_from: from, date_court_to: to }));
                                                }

                                                return newSelected;
                                            });
                                        }}
                                    >
                                        <div>
                                            {month.label}
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={selectedMonths.includes(month.key)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className='description'>Выбор по датам (первые 400 записей)</p>
                            <div className={miniTable.table}>
                                {entries.length===0 ? <WarningPanel text='Ничего не найдено' inTable={true} />
                                : entries.map((entry, index) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={index}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setDateMailing(prev =>
                                                prev.includes(entry.date_court)
                                                    ? prev.filter(d => d !== entry.date_court)
                                                    : [...prev, entry.date_court]
                                            );
                                        }}
                                    >
                                        <div>
                                            {formatDate(entry.date_court)} ({entry.occurrences})
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={dateMailing.includes(entry.date_court)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    </>}

                    {field=='date_am_appointment' &&
                    <>
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Статус подтверждения назначения</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${(!filters.status_am_appointment_approval) && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        status_am_appointment_approval: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='radioSwitchWrapper'>
                                <label className={`radioSwitchLabel ${filters.status_am_appointment_approval === 'Ожидается подтверждение' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='Ожидается подтверждение'
                                        checked={filters.status_am_appointment_approval === 'Ожидается подтверждение'}
                                        onChange={handleChange('status_am_appointment_approval')}
                                    />
                                    <span>Ожидается подтверждение</span>
                                </label>
                                <label className={`radioSwitchLabel ${filters.status_am_appointment_approval === 'Не подтверждено' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='Не подтверждено'
                                        checked={filters.status_am_appointment_approval === 'Не подтверждено'}
                                        onChange={handleChange('status_am_appointment_approval')}
                                    />
                                    <span>Не подтверждено СРО</span>
                                </label>
                                <label className={`radioSwitchLabel ${filters.status_am_appointment_approval === 'Подтверждено' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='Подтверждено'
                                        checked={filters.status_am_appointment_approval === 'Подтверждено'}
                                        onChange={handleChange('status_am_appointment_approval')}
                                    />
                                    <span>Подтверждено СРО</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Дата утверждения управляющего АС</a>
                            <p className='description'>(Резолютивная часть)</p>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${(!filters.date_am_appointment_r_from && !filters.date_am_appointment_r_to) && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        date_am_appointment_r_from: '',
                                        date_am_appointment_r_to: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='inputTwoDateWrapper'>
                                <input type='date' 
                                    value={filters.date_am_appointment_r_from?.split('T')[0]}
                                    onChange={handleChange('date_am_appointment_r_from')} 
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required 
                                />
                                <p>—</p>
                                <input type='date' 
                                    value={filters.date_am_appointment_r_to?.split('T')[0]}
                                    onChange={handleChange('date_am_appointment_r_to')} 
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required 
                                />
                            </div>
                            <p className='description'>Быстрый выбор интервала по крайним 12 месяцам</p>
                            <div className={miniTable.table}>
                                {months.map((month) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={month.key}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setSelectedMonths(prev => {
                                                const newSelected = prev.includes(month.key)
                                                    ? prev.filter(k => k !== month.key)
                                                    : [...prev, month.key];

                                                if (newSelected.length === 0) {
                                                    setFilters(f => ({ ...f, date_am_appointment_r_from: '', date_am_appointment_r_to: '' }));
                                                } else {
                                                    const selectedRanges = months.filter(m => newSelected.includes(m.key));
                                                    const from = selectedRanges.reduce((min, m) => m.from < min ? m.from : min, selectedRanges[0].from);
                                                    const to = selectedRanges.reduce((max, m) => m.to > max ? m.to : max, selectedRanges[0].to);
                                                    setFilters(f => ({ ...f, date_am_appointment_r_from: from, date_am_appointment_r_to: to }));
                                                }

                                                return newSelected;
                                            });
                                        }}
                                    >
                                        <div>
                                            {month.label}
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={selectedMonths.includes(month.key)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className='description'>Выбор по датам (первые 400 записей)</p>
                            <div className={miniTable.table}>
                                {entries.length===0 ? <WarningPanel text='Ничего не найдено' inTable={true} />
                                : entries.map((entry, index) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={index}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setDateMailing(prev =>
                                                prev.includes(entry.date_am_appointment_r)
                                                    ? prev.filter(d => d !== entry.date_am_appointment_r)
                                                    : [...prev, entry.date_am_appointment_r]
                                            );
                                        }}
                                    >
                                        <div>
                                            {formatDate(entry.date_am_appointment_r)} ({entry.occurrences})
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={dateMailing.includes(entry.date_am_appointment_r)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    </>}

                    {field=='status' &&
                    <>
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Статус рассылки опроса</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${(!filters.mailing_status) && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        mailing_status: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='radioSwitchWrapper'>
                                <label className={`radioSwitchLabel ${filters.mailing_status === 'Письма не отправлены' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='Письма не отправлены'
                                        checked={filters.mailing_status === 'Письма не отправлены'}
                                        onChange={handleChange('mailing_status')}
                                    />
                                    <span>Рассылка не совершена</span>
                                </label>
                                <label className={`radioSwitchLabel ${filters.mailing_status === 'Письма отправлены' ? 'selected' : ''}`}>
                                    <input
                                        type='radio'
                                        name='typeSelector'
                                        value='Письма отправлены'
                                        checked={filters.mailing_status === 'Письма отправлены'}
                                        onChange={handleChange('mailing_status')}
                                    />
                                    <span>Рассылка совершена</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    {(filters.mailing_status=='Письма отправлены') &&
                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Дата рассылки опроса</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${(!filters.date_mailing_from && !filters.date_mailing_to) && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        date_mailing_from: '',
                                        date_mailing_to: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div
                                className='panel withHover'
                                style={{position: 'sticky', top: '-10px'}}
                                onClick={() => setIsDateMailingOpen(!isDateMailingOpen)} 
                            >
                                <span
                                    className='horizontalContainer'
                                >
                                    <a>Дата рассылки опроса</a>
                                    <span
                                        className='horizontalContainer'
                                    >
                                        {(filters.date_mailing_from || filters.date_mailing_to) &&
                                        <div style={{width: '7px', height: '7px', borderRadius: '9999px', backgroundColor: 'blue'}}></div>
                                        }
                                        <IconContainer 
                                            Icon={isDateMailingOpen ? CaretUpIcon : CaretDownIcon} 
                                            isClickable={false}
                                            size = '16' 
                                            color = 'black' 
                                            className = '' 
                                        />
                                    </span>
                                </span>
                            </div>
                            {isDateMailingOpen &&
                            <>
                            <div className='inputTwoDateWrapper'>
                                <input type='date' 
                                    value={filters.date_mailing_from?.split('T')[0]}
                                    onChange={handleChange('date_mailing_from')} 
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required 
                                />
                                <p>—</p>
                                <input type='date' 
                                    value={filters.date_mailing_to?.split('T')[0]}
                                    onChange={handleChange('date_mailing_to')} 
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required 
                                />
                            </div>
                            <p className='description'>Быстрый выбор интервала по крайним 12 месяцам</p>
                            <div className={miniTable.table}>
                                {months.map((month) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={month.key}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setSelectedMonths(prev => {
                                                const newSelected = prev.includes(month.key)
                                                    ? prev.filter(k => k !== month.key)
                                                    : [...prev, month.key];

                                                if (newSelected.length === 0) {
                                                    setFilters(f => ({ ...f, date_mailing_from: '', date_mailing_to: '' }));
                                                } else {
                                                    const selectedRanges = months.filter(m => newSelected.includes(m.key));
                                                    const from = selectedRanges.reduce((min, m) => m.from < min ? m.from : min, selectedRanges[0].from);
                                                    const to = selectedRanges.reduce((max, m) => m.to > max ? m.to : max, selectedRanges[0].to);
                                                    setFilters(f => ({ ...f, date_mailing_from: from, date_mailing_to: to }));
                                                }

                                                return newSelected;
                                            });
                                        }}
                                    >
                                        <div>
                                            {month.label}
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={selectedMonths.includes(month.key)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className='description'>Выбор по датам (первые 400 записей)</p>
                            <div className={miniTable.table}>
                                {entries.length===0 ? <WarningPanel text='Ничего не найдено' inTable={true} />
                                : entries.map((entry, index) => (
                                    <div
                                        className={`${miniTable.line} ${miniTable.lineWithHover}`}
                                        key={index}
                                        style={{ gridTemplateColumns: '99fr 1fr' }}
                                        onClick={() => {
                                            setDateMailing(prev =>
                                                prev.includes(entry.date_mailing)
                                                    ? prev.filter(d => d !== entry.date_mailing)
                                                    : [...prev, entry.date_mailing]
                                            );
                                        }}
                                    >
                                        <div>
                                            {formatDate(entry.date_mailing)} ({entry.occurrences})
                                        </div>
                                        <div className={miniTable.actions}>
                                            <input 
                                                type='checkbox'
                                                checked={dateMailing.includes(entry.date_mailing)}
                                                readOnly={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            </>}
                        </div>
                    </div>}

                    <span className='horizontalLine'></span>

                    <div className='labelInputWrapper'>
                        <div className='label'>
                            <a>Статус</a>
                            <p 
                                style={{fontSize: '11px'}}
                                className={`aWithLink ${!filters.status && 'disabledTextButton'}`}
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        status: '',
                                    }));
                                }}
                            >
                                Сбросить
                            </p>
                        </div>
                        <div className='labelInputRight'>
                            <div className='labelInputRight'>
                                <div className='radioSwitchWrapper verticalRSWrapper'>
                                    <label className={`radioSwitchLabel ${filters.status === 'Заявка' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Заявка'
                                            checked={filters.status === 'Заявка'}
                                            onChange={handleChange('status')}
                                        />
                                        <span>Заявка</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${filters.status === 'Управляющий не назначен' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Управляющий не назначен'
                                            checked={filters.status === 'Управляющий не назначен'}
                                            onChange={handleChange('status')}
                                        />
                                        <span>Управляющий не назначен</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${filters.status === 'В работе' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='В работе'
                                            checked={filters.status === 'В работе'}
                                            onChange={handleChange('status')}
                                        />
                                        <span>В работе (Управляющий выбран)</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${filters.status === 'Управляющий отстранен' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Управляющий отстранен'
                                            checked={filters.status === 'Управляющий отстранен'}
                                            onChange={handleChange('status')}
                                        />
                                        <span>Управляющий отстранен</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${filters.status === 'Управляющий освобожден' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Управляющий освобожден'
                                            checked={filters.status === 'Управляющий освобожден'}
                                            onChange={handleChange('status')}
                                        />
                                        <span>Управляющий освобожден</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${filters.status === 'Завершено' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Завершено'
                                            checked={filters.status === 'Завершено'}
                                            onChange={handleChange('status')}
                                        />
                                        <span>Завершено</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                    </>}
                </div>
                <div className={styles.bottom}>
                    {field!==null &&
                    <>
                    <div>Выберите параметры</div>
                    <div className='horizontalContainer'>
                        <button 
                            type='button' 
                            className='button optionalButton red' 
                            onClick={resetFilters}
                        >
                            Сбросить все фильтры
                        </button>
                        <button 
                            type='button' 
                            className='button' 
                            onClick={addFilter}
                        >
                            Применить фильтр
                        </button>
                    </div>
                    </>}
                </div>
            </motion.div>
        </div>
    )
}
